import { useState } from "react";
import { Modal } from "react-bootstrap";
import { Alphabet, At, Asterisk } from "react-bootstrap-icons";
import { useAtom } from "jotai";
import { authAtom } from "./Data/Atoms";
import { Api } from "./Data/Api";
import { parseParker } from "./Data/ApiParse";
import { Input } from "./Controls/Input";
import { Button } from "./Controls/Button";
import { HeaderText } from "./Controls/HeaderText";

interface UserProfileEditModalProps {
    show:    boolean;
    onClose: () => void;
}

export function UserProfileEditModal( { show, onClose }: UserProfileEditModalProps ) {
    const [auth, setAuth] = useAtom( authAtom );

    const [name,            setName           ] = useState( auth.account?.name         ?? "" );
    const [email,           setEmail          ] = useState( auth.account?.emailAddress ?? "" );
    const [currentPassword, setCurrentPassword] = useState( "" );
    const [error,           setError          ] = useState<string | null>( null );
    const [saving,          setSaving         ] = useState( false );

    const emailChanged = email.trim() !== ( auth.account?.emailAddress ?? "" );

    const handleSave = async () => {
        setError( null );
        setSaving( true );
        try {
            const nameChanged = name.trim() !== ( auth.account?.name ?? "" );

            if ( nameChanged ) {
                const res = await Api.changeName( { name: name.trim() } );
                if ( !res.isOk ) { setError( res.error ); setSaving( false ); return; }
                setAuth( { isLoggedIn: true, account: parseParker( res.value ) } );
            }

            if ( emailChanged ) {
                const res = await Api.changeEmailAddress( { currentPassword, newEmailAddress: email.trim() } );
                if ( !res.isOk ) { setError( res.error ); setSaving( false ); return; }
                setAuth( { isLoggedIn: true, account: parseParker( res.value ) } );
            }

            onClose();
        } finally {
            setSaving( false );
        }
    };

    const handleHide = () => {
        setName( auth.account?.name ?? "" );
        setEmail( auth.account?.emailAddress ?? "" );
        setCurrentPassword( "" );
        setError( null );
        onClose();
    };

    return (
        <Modal centered show={show} onEscapeKeyDown={handleHide}>
            <Modal.Header>
                <HeaderText>Edit Profile</HeaderText>
            </Modal.Header>
            <Modal.Body>
                <Input
                    type="text"
                    icon={<Alphabet />}
                    label="Full Name"
                    value={name}
                    onChange={e => setName( e.target.value )}
                />
                <Input
                    type="email"
                    icon={<At />}
                    label="Email Address"
                    value={email}
                    onChange={e => setEmail( e.target.value )}
                />
                {emailChanged && (
                    <Input
                        type="password"
                        icon={<Asterisk />}
                        label="Current Password (required to change email)"
                        value={currentPassword}
                        onChange={e => setCurrentPassword( e.target.value )}
                    />
                )}
                {error && <div className="text-danger small mt-1">{error}</div>}
                <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button className="w-50" onClick={handleHide} disabled={saving}>
                        Cancel
                    </Button>
                    <Button className="w-50 btn-main" onClick={handleSave} disabled={saving}>
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}
